import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAuditTrailByid } from '../../../../redux/auditTrailSlice'
import { useS3Data } from '../../../../hooks/useS3Data'
import {
  buildItemCatPreviewAppBarTitle,
  resolveItemCatPreviewNode,
} from './itemCatTypePreviewResolve'

/* ── normalise field names so downstream components work ── */
const normalizeVersionContent = (raw) => {
  if (!raw || Object.keys(raw).length === 0) return null
  const vc = { ...raw }
  if (!vc.partNumber && vc.part_number) vc.partNumber = vc.part_number
  if (!vc.revision && vc.procedure_rev) vc.revision = vc.procedure_rev
  if (!vc.status && vc.procedure_status) vc.status = vc.procedure_status
  return vc
}

/* ── helper: patch a single type_info object ── */
const patchTi = (ti) => {
  if (ti && !ti.name && ti.type_name) return { ...ti, name: ti.type_name }
  return ti
}

/* ── normalise type_info.name inside parts_required entries ── */
const normalizeParts = (parts) => {
  if (!Array.isArray(parts)) return parts
  return parts.map((pr) => {
    const ti = pr?.part_info?.type_info
    if (!ti) return pr
    const patched = patchTi(ti)
    return patched === ti ? pr : { ...pr, part_info: { ...pr.part_info, type_info: patched } }
  })
}

/* ── normalise type_info inside canvas child nodes ── */
const normalizeCanvasChild = (child) => {
  if (child?.type !== 'canva') return child
  const detail = child.detail
  if (!detail) return child
  let changed = false
  let newDetail = { ...detail }

  // ctm_data → text_boxes → text_box_content → part_info.type_info
  if (Array.isArray(detail.ctm_data)) {
    newDetail.ctm_data = detail.ctm_data.map((page) => {
      if (!Array.isArray(page.text_boxes)) return page
      return {
        ...page,
        text_boxes: page.text_boxes.map((tb) => {
          if (!Array.isArray(tb.text_box_content)) return tb
          return {
            ...tb,
            text_box_content: tb.text_box_content.map((tbc) => {
              const ti = tbc?.part_info?.type_info
              if (!ti) return tbc
              const patched = patchTi(ti)
              return patched === ti ? tbc : { ...tbc, part_info: { ...tbc.part_info, type_info: patched } }
            }),
          }
        }),
      }
    })
    changed = true
  }

  // current_canvas_data_json → part_details → type_info
  const pd = detail.current_canvas_data_json?.part_details
  if (Array.isArray(pd)) {
    newDetail.current_canvas_data_json = {
      ...detail.current_canvas_data_json,
      part_details: pd.map((p) => {
        const ti = p?.type_info
        if (!ti) return p
        const patched = patchTi(ti)
        return patched === ti ? p : { ...p, type_info: patched }
      }),
    }
    changed = true
  }

  return changed ? { ...child, detail: newDetail } : child
}

/* ── patch type_info.name across steps and their children ── */
const normalizeTypeInfo = (sections) =>
  sections.map((sec) => ({
    ...sec,
    steps: (sec.steps ?? []).map((step) => {
      let newStep = { ...step }
      // step parts_required
      const parts = step?.detail?.step_data?.parts_required
      if (Array.isArray(parts)) {
        newStep = {
          ...newStep,
          detail: {
            ...newStep.detail,
            step_data: { ...newStep.detail.step_data, parts_required: normalizeParts(parts) },
          },
        }
      }
      // step-level children (canvas, etc.)
      if (Array.isArray(step.children)) {
        newStep.children = step.children.map(normalizeCanvasChild)
      }
      // substep-level children
      if (Array.isArray(step.substeps)) {
        newStep.substeps = step.substeps.map((sub) => {
          if (!Array.isArray(sub?.children)) return sub
          return { ...sub, children: sub.children.map(normalizeCanvasChild) }
        })
      }
      return newStep
    }),
  }))

/* ── parse snapshot envelope → versionContent + sections ── */
const parseSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return { versionContent: null, sections: [] }
  }
  const { data, ...rest } = snapshot
  const rawSections = Array.isArray(data) ? data : []
  return {
    versionContent: normalizeVersionContent(rest),
    sections: normalizeTypeInfo(rawSections),
  }
}

/* ── build checkbox highlighting state ── */
const buildCheckboxState = (resolvedType, previewTreeNodeId) => {
  if (!resolvedType) return null
  return {
    currentNodeId: previewTreeNodeId,
    currentNodeType: resolvedType,
    sourceNodeId: null,
    allChildrenChecked: resolvedType === 'section',
    auditPreviewCurrentNode: true,
  }
}

/* ── main hook ── */
const useItemCatTypeUpdate = (auditTrailId) => {
  const dispatch = useDispatch()

  const [auditTrailData, setAuditTrailData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(null)

    dispatch(getAuditTrailByid({ id: auditTrailId }))
      .unwrap()
      .then((data) => {
        if (isMounted) setAuditTrailData(data?.results ?? data)
      })
      .catch((err) => {
        if (isMounted) setError(err?.message || err || 'Failed to fetch audit trail data')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => { isMounted = false }
  }, [dispatch, auditTrailId])

  const {
    data: newSnapshotData,
    loading: newSnapshotLoading,
    error: newSnapshotError,
  } = useS3Data(auditTrailData?.new_content_snapshot)

  const {
    data: prevSnapshotData,
    loading: prevSnapshotLoading,
    error: prevSnapshotError,
  } = useS3Data(auditTrailData?.previous_content_snapshot)

  const auditVc = auditTrailData?.version_content ?? {}
  const previewTreeNodeId =
    auditVc.preview_tree_node_id ?? newSnapshotData?.tree_node_id ?? null

  const { versionContent, sections: currentSections } = useMemo(
    () => parseSnapshot(newSnapshotData),
    [newSnapshotData],
  )

  const { sections: previousSections } = useMemo(
    () => parseSnapshot(prevSnapshotData),
    [prevSnapshotData],
  )

  const resolved = useMemo(
    () => resolveItemCatPreviewNode(currentSections, previewTreeNodeId),
    [currentSections, previewTreeNodeId],
  )

  const checkboxState = useMemo(
    () => buildCheckboxState(resolved.type, previewTreeNodeId),
    [resolved.type, previewTreeNodeId],
  )

  const displayTitle = useMemo(
    () => buildItemCatPreviewAppBarTitle(auditTrailData?.version_id, resolved),
    [auditTrailData?.version_id, resolved],
  )

  const snapshotLoading = newSnapshotLoading || prevSnapshotLoading
  const snapshotError = newSnapshotError || prevSnapshotError

  return {
    auditTrailData,
    loading,
    error,
    snapshotLoading,
    snapshotError,
    versionContent,
    sections: currentSections,
    previousSections,
    resolved,
    checkboxState,
    displayTitle,
    previewTreeNodeId,
    hasSnapshotUrl: !!auditTrailData?.new_content_snapshot,
    part_ref: currentSections?.[0]?.part_ref,
    procedure_rev_ref: currentSections?.[0]?.procedure_rev_ref,
    new_content_snapshot: auditTrailData?.new_content_snapshot,
    cancel_screen_data: {
      ...versionContent,
      data: currentSections,
    },
  }
}

export default useItemCatTypeUpdate
